import React, { useCallback } from 'react';
import { View } from 'react-native';

import {
	doesPeopleOnboardingAnswerAllowComment,
	isPeopleOnboardingAnswerCommentComplete,
	isPeopleOnboardingAnswerComplete,
	normalizePeopleOnboardingAnswerValue,
	PEOPLE_ONBOARDING_QUESTION_TYPES,
} from '../../../common/utils/people/onboardingQuestions';
import {
	Picker,
	SwitchWithLabel,
	Text,
	TextInput,
	WarningCallout,
} from '../../components';
import { SecondaryButton } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const resolveQuestionValidationMessage = (question) => {
	const questionType = question?.type || PEOPLE_ONBOARDING_QUESTION_TYPES.TEXT;

	if (questionType === PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX) {
		return 'Tick to confirm.';
	}

	if (questionType === PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX_GROUP) {
		return question?.allowMultiple === false ? 'Select an option.' : 'Select at least one option.';
	}

	if (questionType === PEOPLE_ONBOARDING_QUESTION_TYPES.DROPDOWN) {
		return 'Select an option.';
	}

	return 'Answer this question.';
};

const CompanyDocumentFormQuestionFields = ({
	description = '',
	questions = [],
	answersById = {},
	onChangeAnswer,
	onChangeComment,
	disabled = false,
	showValidation = false,
}) => {
	const styles = useThemedStyles(createStyles);

	const toggleOption = useCallback((question, option, isSelected) => {
		const questionId = String(question?.id || '').trim();
		if (!questionId || !onChangeAnswer) {
			return;
		}

		const currentValue = normalizePeopleOnboardingAnswerValue(
			question,
			answersById?.[questionId]?.value,
		);
		if (question.allowMultiple === false) {
			onChangeAnswer(questionId, isSelected ? option : '');
			return;
		}

		const selectedValues = Array.isArray(currentValue) ? currentValue : [];
		onChangeAnswer(questionId, isSelected
			? [...selectedValues, option]
			: selectedValues.filter(value => value !== option));
	}, [answersById, onChangeAnswer]);

	const hasValidationErrors = showValidation && questions.some((question) => {
		const questionId = String(question?.id || '').trim();
		if (!questionId) {
			return false;
		}

		const answer = answersById?.[questionId];
		return !isPeopleOnboardingAnswerComplete(question, answer?.value) ||
			!isPeopleOnboardingAnswerCommentComplete(question, answer?.value, answer?.comment);
	});

	return (
		<View>
			{Boolean(String(description || '').trim()) && (
				<View style={styles.descriptionBox}>
					<Text style={styles.description}>{String(description).trim()}</Text>
				</View>
			)}
			{questions.map((question) => {
				const questionId = String(question?.id || '').trim();
				if (!questionId) {
					return null;
				}

				const questionType = question?.type || PEOPLE_ONBOARDING_QUESTION_TYPES.TEXT;
				if (questionType === PEOPLE_ONBOARDING_QUESTION_TYPES.HEADING) {
					return (
						<View key={questionId} style={styles.headingRow}>
							<Text style={styles.sectionHeading}>{question.label}</Text>
							<View style={styles.headingLine} />
						</View>
					);
				}

				const rawValue = answersById?.[questionId]?.value;
				const normalizedValue = normalizePeopleOnboardingAnswerValue(question, rawValue);
				const commentValue = String(answersById?.[questionId]?.comment || '');
				const requiresComment = doesPeopleOnboardingAnswerAllowComment(question, normalizedValue);
				const answerMissing = showValidation &&
					!isPeopleOnboardingAnswerComplete(question, normalizedValue);
				const commentMissing = showValidation &&
					!isPeopleOnboardingAnswerCommentComplete(question, normalizedValue, commentValue);
				const options = Array.isArray(question?.options) ? question.options : [];

				const questionInput = (() => {
					if (questionType === PEOPLE_ONBOARDING_QUESTION_TYPES.DROPDOWN) {
						return (
							<>
								<Picker
									selectedValue={typeof normalizedValue === 'string' ? normalizedValue : null}
									onValueChange={value => onChangeAnswer?.(questionId, value)}
									options={options.map(option => ({ label: option, value: option }))}
									emptyValueLabel="Select option"
									disabled={disabled}
									width="100%"
									styles={styles.picker}
								/>
								{Boolean(normalizedValue) && !question.required && (
									<SecondaryButton
										label="Clear selection"
										onPress={() => onChangeAnswer?.(questionId, '')}
										disabled={disabled}
										variant="tonal"
										style={styles.clearButton}
									/>
								)}
							</>
						);
					}

					if (questionType === PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX) {
						return (
							<SwitchWithLabel
								label="Tick to confirm"
								value={Boolean(normalizedValue)}
								onValueChange={value => onChangeAnswer?.(questionId, value)}
								disabled={disabled}
							/>
						);
					}

					if (questionType === PEOPLE_ONBOARDING_QUESTION_TYPES.CHECKBOX_GROUP) {
						const selectedValues = question.allowMultiple === false
							? (normalizedValue ? [normalizedValue] : [])
							: (Array.isArray(normalizedValue) ? normalizedValue : []);

						return (
							<View style={styles.optionGroup}>
								{options.map(option => (
									<SwitchWithLabel
										key={`${questionId}_${option}`}
										label={option}
										value={selectedValues.includes(option)}
										onValueChange={isSelected => toggleOption(question, option, isSelected)}
										disabled={disabled}
									/>
								))}
							</View>
						);
					}

					return (
						<TextInput
							value={rawValue === undefined || rawValue === null ? '' : String(rawValue)}
							onChange={value => onChangeAnswer?.(questionId, value)}
							placeholder={question?.placeholder || 'Type your answer'}
							disabled={disabled}
							maxLength={200}
							style={styles.textInput}
						/>
					);
				})();

				return (
					<View
						key={questionId}
						style={[styles.questionBlock, (answerMissing || commentMissing) && styles.questionBlockError]}
					>
						<Text style={styles.questionLabel}>
							{question.label}
							{question.required ? <Text style={styles.required}> *</Text> : null}
						</Text>
						{questionInput}
						{answerMissing && (
							<Text style={styles.errorText}>{resolveQuestionValidationMessage(question)}</Text>
						)}
						{requiresComment && (
							<View style={styles.commentField}>
								<Text style={styles.commentLabel}>Add a comment *</Text>
								<TextInput
									value={commentValue}
									onChange={value => onChangeComment?.(questionId, value)}
									placeholder="Add a comment"
									disabled={disabled}
									multiline={true}
									numberOfLines={3}
									maxLength={500}
									style={styles.commentInput}
								/>
								{commentMissing && <Text style={styles.errorText}>Add a comment.</Text>}
							</View>
						)}
					</View>
				);
			})}
			{hasValidationErrors && (
				<WarningCallout text="Complete the highlighted questions before submitting." />
			)}
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	descriptionBox: {
		backgroundColor: colors.raised,
		borderColor: colors.raisedBorder,
		borderRadius: radius.card,
		borderWidth: 1,
		marginBottom: space.md,
		padding: space.md,
	},
	description: {
		...type.secondary,
		color: colors.textSecondary,
	},
	headingRow: {
		alignItems: 'center',
		flexDirection: 'row',
		gap: space.md,
		marginBottom: space.xxs,
		marginTop: space.md,
	},
	sectionHeading: {
		...type.rowTitleSemibold,
		color: colors.text,
		flexShrink: 1,
	},
	headingLine: {
		borderTopColor: colors.border,
		borderTopWidth: 1,
		flex: 1,
	},
	questionBlock: {
		borderBottomColor: colors.border,
		borderBottomWidth: 1,
		marginTop: space.sm,
		paddingBottom: space.sm,
	},
	questionBlockError: {
		borderColor: colors.danger,
		backgroundColor: colors.dangerBg,
		borderRadius: radius.card,
		borderWidth: 1,
		paddingHorizontal: space.sm,
		paddingTop: space.sm,
	},
	questionLabel: {
		...type.bodySmallSemibold,
		color: colors.text,
		marginBottom: space.sm,
	},
	required: {
		color: colors.danger,
	},
	picker: {
		marginBottom: space.sm,
	},
	clearButton: {
		alignSelf: 'flex-start',
		marginBottom: space.xs,
	},
	optionGroup: {
		marginBottom: space.xxs,
	},
	textInput: {
		marginBottom: space.xxs,
	},
	commentField: {
		marginTop: space.sm,
	},
	commentLabel: {
		...type.secondaryStrong,
		color: colors.textSecondary,
		marginBottom: space.xs,
	},
	commentInput: {
		minHeight: 72,
		marginBottom: space.xxs,
		textAlignVertical: 'top',
	},
	errorText: {
		...type.caption,
		color: colors.danger,
		marginBottom: space.xs,
	},
});

export default CompanyDocumentFormQuestionFields;
